import React, { useMemo, useState } from 'react';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { useParams, useNavigate } from '@tanstack/react-router';
import {
  useShareableLinkByToken,
  useDeleteShareableLink,
} from 'src/api/core/useShareableLinks';
import { TableSkeleton } from '@/components/common/loading-skeletons/table-skeleton';
import {
  WarningCircleIcon,
  LinkBreakIcon,
  ClockIcon,
  PencilIcon,
  TrashIcon,
  ArrowSquareOutIcon,
} from '@phosphor-icons/react';
import {
  AlertContainer,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import {
  useDivisionRebateSummary,
  useSupplierTagSummary,
  useDailyEarningsPYE,
  useMonthlyTrending,
  useSupplierPaymentTerms,
  useProfitabilityReport,
} from '@/api/rebates/useReports';
import { ReportTableWrapper } from '@/components/reports/ReportTableWrapper';
import { ReportTable } from '@/components/reports/ReportTable';
import GentlyTable, { type TableColumn } from '@/components/common/GentlyTable';
import type {
  ReportLineType,
  ReportTimeFilter,
  SupplierPaymentTermsItem,
  ProfitabilityReportRow,
} from '@erp/shared-types';
import { CardHeader, CardTitle } from '@/components/ui/card';
import { formatCurrencyAdvanced } from '@erp/shared-utils';
import useDivisions from '@/api/organization/useDivisions';
import { LINE_TYPE_LABELS } from '@/types/rebateAnalytics';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EditShareableLinkModal } from '@/components/reports/EditShareableLinkModal';
import paths from '@/paths';

type ReportType =
  | 'division-rebate-summary'
  | 'supplier-tag-summary'
  | 'daily-earnings-pye'
  | 'monthly-trending'
  | 'supplier-payment-terms'
  | 'profitability-report';

interface ReportMetadata {
  year?: number;
  divisionIds?: string[];
  lineType?: ReportLineType;
  supplierTagId?: string;
  categoryFilters?: string[];
  viewMode?:
    | 'supplier'
    | 'division'
    | 'productFamilyDesc'
    | 'divisionHierarchy';
  month?: number | null;
}

const VALID_REPORT_TYPES: ReportType[] = [
  'division-rebate-summary',
  'supplier-tag-summary',
  'daily-earnings-pye',
  'monthly-trending',
  'supplier-payment-terms',
  'profitability-report',
];

function parseResourceType(resourceType: string): ReportType | null {
  // Format: "report:division-rebate-summary"
  if (!resourceType.startsWith('report:')) return null;
  const reportId = resourceType.replace('report:', '');
  if (VALID_REPORT_TYPES.includes(reportId as ReportType)) {
    return reportId as ReportType;
  }
  return null;
}

// Helper to format currency
const formatCurrencyWhole = (value: number): string => {
  return formatCurrencyAdvanced(value, 'USD', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const formatPercent = (value: number): string => {
  return `${value.toFixed(2)}%`;
};

function SharedReportPage() {
  const { token } = useParams({ strict: false }) as { token: string };
  const navigate = useNavigate();
  const [timeFilter, setTimeFilter] = useState<ReportTimeFilter>('all');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const {
    data: linkData,
    isLoading: linkLoading,
    error: linkError,
  } = useShareableLinkByToken(token);

  const shareableLink = linkData?.shareableLink;
  const isCreator = linkData?.isCreator ?? false;
  const reportType = shareableLink
    ? parseResourceType(shareableLink.resourceType)
    : null;
  const metadata = (shareableLink?.metadata || {}) as ReportMetadata;

  // Delete link mutation
  const deleteLink = useDeleteShareableLink(shareableLink?.id ?? '');

  // Get the original report path
  const getOriginalReportPath = () => {
    switch (reportType) {
      case 'division-rebate-summary':
        return paths.authenticated.rebates.divisionRebateSummary;
      case 'supplier-tag-summary':
        return paths.authenticated.rebates.supplierTagSummary;
      case 'daily-earnings-pye':
        return paths.authenticated.rebates.dailyEarningsPYE;
      case 'monthly-trending':
        return paths.authenticated.rebates.monthlyTrending;
      case 'supplier-payment-terms':
        return paths.authenticated.rebates.supplierPaymentTerms;
      case 'profitability-report':
        return paths.authenticated.rebates.profitability;
      default:
        return paths.authenticated.rebates.reports;
    }
  };

  const handleDelete = async () => {
    await deleteLink.mutateAsync(undefined as unknown as void);
    setDeleteDialogOpen(false);
    // Navigate to reports page after deletion
    navigate({ to: paths.authenticated.rebates.reports });
  };

  // Extract filters from metadata
  const currentYear = new Date().getFullYear();
  const year = metadata.year ?? currentYear;
  const divisionIds = metadata.divisionIds;
  const lineType = metadata.lineType ?? 'reported';

  // Fetch divisions to map IDs to names for display
  const { data: divisionsData } = useDivisions(true);

  // Get division info with tags for smart grouping display
  const divisionDisplayInfo = useMemo(() => {
    if (!divisionsData?.divisions || !divisionIds || divisionIds.length === 0) {
      return { type: 'all' as const, display: null };
    }

    const selectedDivisions = divisionsData.divisions.filter((div) =>
      divisionIds.includes(div.id),
    );

    if (selectedDivisions.length === 0) {
      return { type: 'all' as const, display: null };
    }

    // Group all divisions by their tag
    const divisionsByTag = new Map<string, string[]>();
    const untaggedDivisions: string[] = [];

    divisionsData.divisions.forEach((div) => {
      const tagName = div.tags?.[0]?.name;
      if (tagName) {
        if (!divisionsByTag.has(tagName)) {
          divisionsByTag.set(tagName, []);
        }
        divisionsByTag.get(tagName)!.push(div.id);
      } else {
        untaggedDivisions.push(div.id);
      }
    });

    // Check if entire tags/banners are selected
    const selectedTags: string[] = [];
    const remainingDivisionIds = new Set(divisionIds);

    divisionsByTag.forEach((tagDivisionIds, tagName) => {
      // Check if ALL divisions in this tag are selected
      const allSelected = tagDivisionIds.every((id) =>
        remainingDivisionIds.has(id),
      );
      if (allSelected && tagDivisionIds.length > 0) {
        selectedTags.push(tagName);
        // Remove these from remaining
        tagDivisionIds.forEach((id) => remainingDivisionIds.delete(id));
      }
    });

    // Get names of remaining individual divisions
    const remainingDivisionNames = selectedDivisions
      .filter((div) => remainingDivisionIds.has(div.id))
      .map((div) => div.name);

    // Build display
    const displayParts: string[] = [...selectedTags, ...remainingDivisionNames];

    if (displayParts.length === 0) {
      return { type: 'all' as const, display: null };
    }

    if (displayParts.length === 1) {
      return { type: 'single' as const, display: displayParts[0] };
    }

    if (displayParts.length <= 3) {
      return { type: 'list' as const, display: displayParts };
    }

    return {
      type: 'count' as const,
      display: `${selectedTags.length > 0 ? `${selectedTags.length} banner(s)` : ''}${selectedTags.length > 0 && remainingDivisionNames.length > 0 ? ', ' : ''}${remainingDivisionNames.length > 0 ? `${remainingDivisionNames.length} division(s)` : ''}`,
    };
  }, [divisionsData?.divisions, divisionIds]);

  // Fetch reports based on type
  const divisionSummary = useDivisionRebateSummary({
    year,
    divisionIds,
    lineType,
  });

  const supplierTagSummary = useSupplierTagSummary({
    year,
    divisionIds,
    supplierTagId: metadata.supplierTagId,
    lineType,
  });

  const dailyEarningsPYE = useDailyEarningsPYE({
    year,
    divisionIds,
    categoryFilters: metadata.categoryFilters,
  });

  const monthlyTrending = useMonthlyTrending({
    year,
    divisionIds,
  });

  const supplierPaymentTerms = useSupplierPaymentTerms({
    year,
  });

  const profitabilityReport = useProfitabilityReport({
    year,
    viewMode: metadata.viewMode ?? 'supplier',
    month: metadata.month ?? null,
    divisionIds,
  });

  // Supplier Payment Terms table columns
  const supplierPaymentTermsColumns: TableColumn<SupplierPaymentTermsItem>[] = [
    { id: 'supplier', label: 'Supplier', sortable: true },
    { id: 'agreement', label: 'Agreement', sortable: true },
    { id: 'rebatePaymentTerms', label: 'Rebate Payment Terms', sortable: true },
    {
      id: 'purchasePaymentTerms',
      label: 'Purchase Payment Terms',
      sortable: true,
    },
  ];

  // Profitability Report table columns
  const profitabilityColumns: TableColumn<ProfitabilityReportRow>[] = [
    {
      id: 'supplierName',
      label: 'Supplier',
      sortable: true,
      render: (row) =>
        row.supplierName || row.divisionName || row.productFamilyDesc || '-',
    },
    {
      id: 'cogs',
      label: 'COGS ($)',
      sortable: true,
      render: (row) => formatCurrencyWhole(row.cogs),
    },
    {
      id: 'landedMarginPercent',
      label: 'Landed Margin (%)',
      sortable: true,
      render: (row) => formatPercent(row.landedMarginPercent),
    },
    {
      id: 'totalRebates',
      label: 'Total Rebates ($)',
      sortable: true,
      render: (row) => formatCurrencyWhole(row.totalRebates),
    },
    {
      id: 'totalRebatesPercent',
      label: 'Total Rebates (%)',
      sortable: true,
      render: (row) => formatPercent(row.totalRebatesPercent),
    },
    {
      id: 'totalProfitPercent',
      label: 'Total Profit (%)',
      sortable: true,
      render: (row) => formatPercent(row.totalProfitPercent),
    },
  ];

  // Select the appropriate report data
  const getReportData = () => {
    switch (reportType) {
      case 'division-rebate-summary':
        return {
          data: divisionSummary.data,
          isLoading: divisionSummary.isLoading,
          error: divisionSummary.error,
          title: 'Division Rebate Summary',
        };
      case 'supplier-tag-summary':
        return {
          data: supplierTagSummary.data,
          isLoading: supplierTagSummary.isLoading,
          error: supplierTagSummary.error,
          title: 'Supplier Tag Summary',
        };
      case 'daily-earnings-pye':
        return {
          data: dailyEarningsPYE.data,
          isLoading: dailyEarningsPYE.isLoading,
          error: dailyEarningsPYE.error,
          title: 'Daily Earnings PYE',
        };
      case 'monthly-trending':
        return {
          data: monthlyTrending.data,
          isLoading: monthlyTrending.isLoading,
          error: monthlyTrending.error,
          title: 'Monthly Trending',
        };
      case 'supplier-payment-terms':
        return {
          data: supplierPaymentTerms.data,
          isLoading: supplierPaymentTerms.isLoading,
          error: supplierPaymentTerms.error,
          title: 'Supplier Payment Terms',
        };
      case 'profitability-report':
        return {
          data: profitabilityReport.data,
          isLoading: profitabilityReport.isLoading,
          error: profitabilityReport.error,
          title: 'Profitability Report',
        };
      default:
        return {
          data: null,
          isLoading: false,
          error: null,
          title: 'Unknown Report',
        };
    }
  };

  const reportData = getReportData();

  // Build subtitle with filters
  const buildSubtitle = () => {
    const parts: string[] = [year.toString()];

    // Add division info (not applicable for supplier-payment-terms)
    if (reportType !== 'supplier-payment-terms') {
      if (divisionDisplayInfo.type === 'all') {
        parts.push('All Divisions');
      } else if (divisionDisplayInfo.type === 'single') {
        parts.push(divisionDisplayInfo.display as string);
      } else if (divisionDisplayInfo.type === 'list') {
        parts.push((divisionDisplayInfo.display as string[]).join(', '));
      } else if (divisionDisplayInfo.type === 'count') {
        parts.push(divisionDisplayInfo.display as string);
      }
    }

    // Add line type for applicable reports
    if (
      reportType === 'division-rebate-summary' ||
      reportType === 'supplier-tag-summary'
    ) {
      parts.push(LINE_TYPE_LABELS[lineType] || lineType);
    }

    return parts.join(' • ');
  };

  // Loading state for link
  if (linkLoading) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card>
            <CardContent className="py-12">
              <TableSkeleton />
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Error state for link
  if (linkError) {
    const errorMessage =
      linkError instanceof Error
        ? linkError.message
        : 'Unable to access this link';
    const isExpired = errorMessage.toLowerCase().includes('expired');
    const isUnauthorized =
      errorMessage.toLowerCase().includes('access') ||
      errorMessage.toLowerCase().includes('not found');

    return (
      <DashboardLayout>
        <div className="p-6 max-w-2xl mx-auto">
          <AlertContainer variant="destructive">
            {isExpired ? (
              <ClockIcon className="h-4 w-4" />
            ) : isUnauthorized ? (
              <LinkBreakIcon className="h-4 w-4" />
            ) : (
              <WarningCircleIcon className="h-4 w-4" />
            )}
            <AlertTitle>
              {isExpired
                ? 'Link Expired'
                : isUnauthorized
                  ? 'Access Denied'
                  : 'Error'}
            </AlertTitle>
            <AlertDescription>
              {isExpired
                ? 'This shared link has expired. Please request a new link from the report owner.'
                : isUnauthorized
                  ? 'You do not have permission to view this report. Make sure you are logged in with the correct email address.'
                  : errorMessage}
            </AlertDescription>
          </AlertContainer>
        </div>
      </DashboardLayout>
    );
  }

  // Invalid report type
  if (!reportType) {
    return (
      <DashboardLayout>
        <div className="p-6 max-w-2xl mx-auto">
          <AlertContainer variant="destructive">
            <WarningCircleIcon className="h-4 w-4" />
            <AlertTitle>Invalid Report</AlertTitle>
            <AlertDescription>
              This link points to an unknown report type.
            </AlertDescription>
          </AlertContainer>
        </div>
      </DashboardLayout>
    );
  }

  // Get display title - use link name if available, otherwise report type title
  const displayTitle = shareableLink?.name || reportData.title;

  // Render the report
  return (
    <DashboardLayout>
      <div className="p-6 w-full min-w-0 max-w-full overflow-hidden">
        <div className="mb-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge
                  variant="secondary"
                  className="bg-blue-100 text-blue-800"
                >
                  Shared Report
                </Badge>
                {shareableLink?.name && (
                  <span className="text-sm text-muted-foreground">
                    {reportData.title}
                  </span>
                )}
              </div>
              <h1 className="text-2xl font-bold">{displayTitle}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {buildSubtitle()}
              </p>
            </div>

            {/* Creator Actions */}
            {isCreator && shareableLink && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate({ to: getOriginalReportPath() })}
                >
                  <ArrowSquareOutIcon className="h-4 w-4 mr-1" />
                  Original Report
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditModalOpen(true)}
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  <TrashIcon className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Loading State */}
        {reportData.isLoading && (
          <Card>
            <CardContent className="py-12">
              <TableSkeleton />
            </CardContent>
          </Card>
        )}

        {/* Error State */}
        {reportData.error && !reportData.isLoading && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-destructive">
                Error loading report data. Please try again.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Report Content */}
        {reportData.data && !reportData.isLoading && (
          <div className="space-y-6">
            {reportType === 'division-rebate-summary' &&
              divisionSummary.data && (
                <>
                  <ReportTableWrapper
                    table={divisionSummary.data.ruleTagTable}
                    timeFilter={timeFilter}
                    onTimeFilterChange={setTimeFilter}
                    downloadFilename="rebates-by-rule-tag"
                    year={year}
                  />
                  <ReportTableWrapper
                    table={divisionSummary.data.supplierTable}
                    timeFilter={timeFilter}
                    onTimeFilterChange={setTimeFilter}
                    downloadFilename="rebates-by-supplier"
                    year={year}
                  />
                </>
              )}

            {reportType === 'supplier-tag-summary' &&
              supplierTagSummary.data && (
                <ReportTableWrapper
                  table={supplierTagSummary.data.supplierTagTable}
                  timeFilter={timeFilter}
                  onTimeFilterChange={setTimeFilter}
                  downloadFilename="supplier-tag-report"
                  year={year}
                />
              )}

            {reportType === 'daily-earnings-pye' && dailyEarningsPYE.data && (
              <>
                <ReportTableWrapper
                  table={dailyEarningsPYE.data.table}
                  timeFilter="all"
                  onTimeFilterChange={() => {}}
                  showTimeFilter={false}
                  downloadFilename="daily-earnings-pye"
                  year={year}
                />
                <ReportTableWrapper
                  table={dailyEarningsPYE.data.monthlyBreakdownTable}
                  timeFilter="all"
                  onTimeFilterChange={() => {}}
                  showTimeFilter={false}
                  downloadFilename="daily-earnings-pye-monthly-breakdown"
                  year={year}
                />
              </>
            )}

            {reportType === 'monthly-trending' && monthlyTrending.data && (
              <>
                <ReportTable
                  table={monthlyTrending.data.table}
                  timeFilter="all"
                  onTimeFilterChange={() => {}}
                  rowLabel="Division"
                  showTotals={true}
                  showTimeFilter={false}
                />
                <ReportTable
                  table={monthlyTrending.data.monthlyBreakdownTable}
                  timeFilter="all"
                  onTimeFilterChange={() => {}}
                  rowLabel="Division"
                  showTotals={true}
                  showTimeFilter={false}
                />
              </>
            )}

            {reportType === 'supplier-payment-terms' &&
              supplierPaymentTerms.data && (
                <Card>
                  <CardHeader>
                    <CardTitle>Supplier Payment Terms</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <GentlyTable
                      data={supplierPaymentTerms.data.data}
                      columns={supplierPaymentTermsColumns}
                      searchPlaceholder="Search suppliers, agreements, or payment terms..."
                      searchFields={[
                        'supplier',
                        'agreement',
                        'rebatePaymentTerms',
                        'purchasePaymentTerms',
                      ]}
                      emptyMessage="No payment terms found for the selected year"
                      showDownload={true}
                      downloadFilename={`supplier-payment-terms-${year}`}
                      showCount={true}
                      defaultSort={{ sortBy: 'supplier', sortDirection: 'asc' }}
                    />
                  </CardContent>
                </Card>
              )}

            {reportType === 'profitability-report' &&
              profitabilityReport.data && (
                <Card>
                  <CardHeader>
                    <CardTitle>Profitability Report</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {profitabilityReport.data.rows.length === 0 ? (
                      <div className="text-sm text-muted-foreground">
                        No data available. Sales margin data may need to be
                        uploaded for this year.
                      </div>
                    ) : (
                      <GentlyTable
                        data={profitabilityReport.data.rows}
                        columns={profitabilityColumns}
                        searchPlaceholder="Search suppliers or divisions..."
                        searchFields={[
                          'supplierName',
                          'divisionName',
                          'productFamilyDesc',
                        ]}
                        emptyMessage="No profitability data found"
                        showDownload={true}
                        downloadFilename={`profitability-report-${year}`}
                        showCount={true}
                        defaultSort={{
                          sortBy: 'supplierName',
                          sortDirection: 'asc',
                        }}
                      />
                    )}
                  </CardContent>
                </Card>
              )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {shareableLink && (
        <EditShareableLinkModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          link={shareableLink}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => !open && setDeleteDialogOpen(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete shared link?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this shared link. Anyone who has this
              link will no longer be able to access the report.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteLink.isPending}
            >
              {deleteLink.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}

export default SharedReportPage;
